import React, { useState, useEffect } from 'react';
import staticEventosEspeciales from '../data/eventos_especiales.json';
import styles from '../css/CalendarSection.module.css';

const MONTH_NAMES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const CalendarSection = ({ avisosSemana }) => {
    const [displayedDate, setDisplayedDate] = useState(new Date());
    const [selectedDateStr, setSelectedDateStr] = useState(() => {
        const today = new Date();
        const y = today.getFullYear();
        const m = String(today.getMonth() + 1).padStart(2, '0');
        const d = String(today.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    });
    
    const [eventosEspeciales, setEventosEspeciales] = useState([]);

    // Cargar eventos especiales de JSON + localStorage
    useEffect(() => {
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            setEventosEspeciales(staticEventosEspeciales || []);
            return;
        }

        const local = localStorage.getItem('eventos_especiales');
        if (local) {
            try {
                setEventosEspeciales(JSON.parse(local));
            } catch (e) {
                console.error("Error parsing local eventos", e);
                setEventosEspeciales(staticEventosEspeciales || []);
            }
        } else {
            setEventosEspeciales(staticEventosEspeciales || []);
        }
    }, [staticEventosEspeciales]);

    // Combinar y agrupar eventos por fecha
    const getEventsByDateMap = () => {
        const map = {};

        // 1. Normalizar y agrupar avisos de la semana
        if (avisosSemana && Array.isArray(avisosSemana)) {
            avisosSemana.forEach(dia => {
                const fecha = dia.fecha_dia; // YYYY-MM-DD
                if (fecha && Array.isArray(dia.avisos)) {
                    if (!map[fecha]) map[fecha] = [];
                    dia.avisos.forEach((aviso, idx) => {
                        map[fecha].push({
                            id: `semana-${fecha}-${idx}`,
                            titulo: aviso.titulo,
                            descripcion: 'Actividad semanal regular',
                            hora: aviso.hora,
                            lugar: aviso.lugar,
                            tipo: 'semana'
                        });
                    });
                }
            });
        }

        // 2. Combinar eventos especiales
        if (eventosEspeciales && Array.isArray(eventosEspeciales)) {
            eventosEspeciales.forEach(evt => {
                const fecha = evt.fecha; // YYYY-MM-DD
                if (fecha) {
                    if (!map[fecha]) map[fecha] = [];
                    // Evitar duplicados por si acaso
                    const yaExiste = map[fecha].some(existing => existing.id === evt.id || (existing.titulo === evt.titulo && existing.hora === evt.hora));
                    if (!yaExiste) {
                        map[fecha].push(evt);
                    }
                }
            });
        }

        return map;
    };

    const eventsMap = getEventsByDateMap();

    // Obtener días del mes actual
    const year = displayedDate.getFullYear();
    const month = displayedDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    // Index del primer día (0 = Domingo, 1 = Lunes, etc.)
    const firstDayRaw = new Date(year, month, 1).getDay();
    // Ajustar para que Lunes sea 0 y Domingo sea 6
    const firstDayIndex = firstDayRaw === 0 ? 6 : firstDayRaw - 1;

    // Cambiar de mes
    const prevMonth = () => {
        setDisplayedDate(new Date(year, month - 1, 1));
    };

    const nextMonth = () => {
        setDisplayedDate(new Date(year, month + 1, 1));
    };

    // Crear arreglo de celdas
    const cells = [];
    // Celdas vacías del mes anterior (relleno)
    for (let i = 0; i < firstDayIndex; i++) {
        cells.push({ isPadding: true });
    }
    // Celdas del mes actual
    for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        const dayEvents = eventsMap[dateStr] || [];
        cells.push({
            isPadding: false,
            dayNum: d,
            dateStr,
            events: dayEvents
        });
    }

    const selectedEvents = eventsMap[selectedDateStr] || [];

    // Obtener lista de eventos futuros destacados (Nacionales o extraordinarios locales) en los próximos 60 días
    const getDestacados = () => {
        const list = [];
        const hoy = new Date();
        hoy.setHours(0,0,0,0);

        if (eventosEspeciales && Array.isArray(eventosEspeciales)) {
            eventosEspeciales.forEach(evt => {
                const evtFecha = new Date(evt.fecha + 'T00:00:00');
                if (evtFecha >= hoy) {
                    list.push(evt);
                }
            });
        }
        // Ordenar por fecha
        return list.sort((a, b) => new Date(a.fecha) - new Date(b.fecha)).slice(0, 3);
    };

    const destacados = getDestacados();

    return (
        <section className={styles.calendarSection}>
            <div className={styles.container}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>Calendario de Actividades</h2>
                    <p className={styles.sectionSubtitle}>Entérate de nuestras reuniones generales, eventos locales y corporativos nacionales</p>
                </div>

                <div className={styles.calendarGridContainer}>
                    {/* PARTE IZQUIERDA: CALENDARIO MENSUAL */}
                    <div className={styles.calendarBlock}>
                        <div className={styles.calendarHeader}>
                            <button className={styles.navBtn} onClick={prevMonth} aria-label="Mes anterior">
                                <i className="fas fa-chevron-left"></i>
                            </button>
                            <h3 className={styles.monthLabel}>{MONTH_NAMES[month]} {year}</h3>
                            <button className={styles.navBtn} onClick={nextMonth} aria-label="Mes siguiente">
                                <i className="fas fa-chevron-right"></i>
                            </button>
                        </div>

                        <div className={styles.weekdaysRow}>
                            {WEEKDAYS.map((wd, i) => (
                                <div key={i} className={styles.weekdayCell}>{wd}</div>
                            ))}
                        </div>

                        <div className={styles.daysGrid}>
                            {cells.map((cell, idx) => {
                                if (cell.isPadding) {
                                    return <div key={`pad-${idx}`} className={styles.dayCellEmpty}></div>;
                                }

                                const isSelected = cell.dateStr === selectedDateStr;
                                const isToday = (() => {
                                    const today = new Date();
                                    const y = today.getFullYear();
                                    const m = String(today.getMonth() + 1).padStart(2, '0');
                                    const d = String(today.getDate()).padStart(2, '0');
                                    return cell.dateStr === `${y}-${m}-${d}`;
                                })();

                                const hasNacional = cell.events.some(e => e.tipo === 'nacional');
                                const hasLocal = cell.events.some(e => e.tipo === 'local');
                                const hasSemana = cell.events.some(e => e.tipo === 'semana');

                                return (
                                    <button 
                                        key={cell.dateStr} 
                                        className={`${styles.dayCell} ${isSelected ? styles.dayCellSelected : ''} ${isToday ? styles.dayCellToday : ''}`}
                                        onClick={() => setSelectedDateStr(cell.dateStr)}
                                    >
                                        <span className={styles.dayNumber}>{cell.dayNum}</span>
                                        <div className={styles.dotsContainer}>
                                            {hasNacional && <span className={styles.dotNacional}></span>}
                                            {hasLocal && <span className={styles.dotLocal}></span>}
                                            {hasSemana && <span className={styles.dotSemana}></span>}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* PARTE DERECHA: AGENDA DEL DÍA */}
                    <div className={styles.agendaBlock}>
                        <div className={styles.agendaHeader}>
                            <h4>Agenda del Día</h4>
                            <span className={styles.agendaDate}>
                                {(() => {
                                    const parts = selectedDateStr.split('-');
                                    if (parts.length < 3) return '';
                                    const dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
                                    return `${WEEKDAYS[dateObj.getDay() === 0 ? 6 : dateObj.getDay() - 1]} ${parts[2]} de ${MONTH_NAMES[parts[1] - 1]}`;
                                })()}
                            </span>
                        </div>

                        <div className={styles.eventsList}>
                            {selectedEvents.length === 0 ? (
                                <div className={styles.noEvents}>
                                    <i className="fa-light fa-calendar-xmark"></i>
                                    <p>No hay actividades registradas para este día.</p>
                                </div>
                            ) : (
                                selectedEvents.map((evt) => {
                                    const isNacional = evt.tipo === 'nacional';
                                    const isLocal = evt.tipo === 'local';
                                    const isSemana = evt.tipo === 'semana';

                                    let cardClass = styles.eventCardLocal;
                                    let badgeClass = styles.badgeLocal;
                                    let badgeIcon = <i className="fas fa-church"></i>;
                                    let badgeLabel = "Actividad Local";

                                    if (isNacional) {
                                        cardClass = styles.eventCardNacional;
                                        badgeClass = styles.badgeNacional;
                                        badgeIcon = <i className="fas fa-building-columns"></i>;
                                        badgeLabel = "Corporación Nacional";
                                    } else if (isLocal) {
                                        cardClass = styles.eventCardExtraordinario;
                                        badgeClass = styles.badgeExtraordinario;
                                        badgeIcon = <i className="fas fa-star-of-life"></i>;
                                        badgeLabel = "Local Extraordinaria";
                                    } else if (isSemana) {
                                        cardClass = styles.eventCardSemana;
                                        badgeClass = styles.badgeSemana;
                                        badgeIcon = <i className="fas fa-calendar-week"></i>;
                                        badgeLabel = "Actividad Regular";
                                    }

                                    return (
                                        <div 
                                            key={evt.id} 
                                            className={`${styles.eventCard} ${cardClass}`}
                                        >
                                            <div className={styles.eventCardHeader}>
                                                <span className={badgeClass}>
                                                    {badgeIcon} {badgeLabel}
                                                </span>
                                                {evt.hora && (
                                                    <span className={styles.eventTime}>
                                                        <i className="fas fa-clock"></i> {evt.hora.substring(0, 5)} hrs
                                                    </span>
                                                )}
                                            </div>
                                            <h5 className={styles.eventTitle}>{evt.titulo}</h5>
                                            {evt.descripcion && <p className={styles.eventDesc}>{evt.descripcion}</p>}
                                            {evt.lugar && (
                                                <div className={styles.eventPlace}>
                                                    <i className="fas fa-location-dot"></i> <span>{evt.lugar}</span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* BANNER DE PRÓXIMOS EVENTOS DESTACADOS */}
                        {destacados.length > 0 && (
                            <div className={styles.destacadosSection}>
                                <h5><i className="fa-solid fa-star text-warning"></i> Eventos Destacados</h5>
                                <div className={styles.destacadosList}>
                                    {destacados.map(dest => (
                                        <div key={dest.id} className={styles.destacadoItem} onClick={() => setSelectedDateStr(dest.fecha)}>
                                            <div className={styles.destacadoDateBadge}>
                                                <span className={styles.destacadoDay}>{dest.fecha.split('-')[2]}</span>
                                                <span className={styles.destacadoMonth}>{MONTH_NAMES[parseInt(dest.fecha.split('-')[1]) - 1].substring(0, 3)}</span>
                                            </div>
                                            <div className={styles.destacadoInfo}>
                                                <h6>{dest.titulo}</h6>
                                                <span>{dest.tipo === 'nacional' ? 'Corporativo' : 'Local Extraordinario'}</span>
                                            </div>
                                            <i className="fa-solid fa-chevron-right styles.destacadoArrow"></i>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CalendarSection;
